import { buildRegistrationFormDefaults } from "components/Forms/TenantRegistrationFields/registrationFormSchema.ts";
import { FormFieldTypes } from "types/enums.ts";
import { z } from "zod";

import FormFieldData = App.Data.FormFieldData;

export interface TenantUserFormData {
    email: string;
    nickname: string;
    password: string;
    password_confirmation: string;
    commission_group_id: string;
    fields: Record<string, unknown>;
}

const isBlank = (value: unknown) =>
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);

export const buildTenantUserFormSchema = (
    fields: FormFieldData[],
    mode: "create" | "detail"
): z.ZodType<TenantUserFormData> =>
    z
        .object({
            email: z
                .string()
                .min(1, "forms.general-validation.email_required")
                .email("forms.general-validation.email_invalid"),
            nickname: z.string(),
            password: z.string(),
            password_confirmation: z.string(),
            commission_group_id: z.string(),
            fields: z.record(z.unknown()),
        })
        .superRefine((data, context) => {
            if (mode === "create") {
                if (!data.nickname.trim()) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "forms.general-validation.nickname_required",
                        path: ["nickname"],
                    });
                }
                if (data.password.length < 8) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "forms.tenant-user.password_min",
                        path: ["password"],
                    });
                }
                if (data.password !== data.password_confirmation) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "forms.tenant-user.password_confirmation",
                        path: ["password_confirmation"],
                    });
                }
            }

            fields.forEach((field) => {
                if (field.is_hidden || (mode === "detail" && field.global_form_field_id !== null)) return;

                const value = data.fields[field.system_name];
                if (
                    field.is_required &&
                    (isBlank(value) || (field.type === FormFieldTypes.Checkbox && value !== true))
                ) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "forms.general-validation.required",
                        path: ["fields", field.system_name],
                    });
                }
            });
        });

export const buildTenantUserFormDefaults = (
    fields: FormFieldData[],
    values: Record<string, unknown> | undefined,
    user?: App.Data.UserData,
    commissionGroupId = ""
): TenantUserFormData => {
    const registrationDefaults = buildRegistrationFormDefaults(fields);

    return {
        ...registrationDefaults,
        email: user?.email ?? "",
        nickname: user?.nickname ?? "",
        password: "",
        password_confirmation: "",
        commission_group_id: commissionGroupId,
        fields: Object.fromEntries(
            fields.map((field) => [
                field.system_name,
                values === undefined
                    ? registrationDefaults.fields[field.system_name]
                    : (values[field.system_name] ?? null),
            ])
        ),
    };
};
